import {ArticleRepo} from "../api/article.repo";
import {inject, Injectable} from "@angular/core";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {Observable} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {DuckDbHelper, sqlDirection, sqlLike, sqlNumber, sqlText} from "./duckdb.helper";
import {SearchArticleRes} from "../dto/search/search-article.res";
import {map} from "rxjs/operators";
import {isDefined, nonBlank} from "../../libs/utils/epic.utils";
import {LoggingService} from "../../services/logger/logging.service";
import {Additionals} from "../item/additionals";
import {ArticleDto} from "../dto/article.dto";
import {CatalogDto} from "../dto/catalog.dto";
import {GoodsGroupDto} from "../dto/goods-group.dto";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";

@Injectable({providedIn: 'root'})
export class ArticleDuckDb extends ArticleRepo {

  private readonly duck: DuckDbHelper = inject(DuckDbHelper);
  private readonly log: LoggingService = inject(LoggingService);

  public override readCustomGoodsGroup(org: OrgDto): Observable<WrapperDto<GoodsGroupDto>> {
    return this.readAllGoodsGroup(org);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override readAllGoodsGroup(_org: OrgDto): Observable<WrapperDto<GoodsGroupDto>> {
    const select = `
      SELECT g.id          AS id,
             g.name        AS name,
             g.source      AS source,
             g.external_id AS externalId
      FROM goods_group g`;
    return this.duck.sql(select).pipe(map((res) => {
      if (DuckDbHelper.hasRes(res)) {
        return DuckDbHelper.datas('GoodsGroupDto', res.map((r: any) => new GoodsGroupDto(r)));
      }
      return DuckDbHelper.datas('GoodsGroupDto', []);
    }));
  }

  public override readCustomCatalogs(org: OrgDto): Observable<WrapperDto<CatalogDto>> {
    return this.readAllCatalogs(org);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override readAllCatalogs(_org: OrgDto): Observable<WrapperDto<CatalogDto>> {
    const selectCatalog = `
      SELECT a.catalog_id   AS id,
             a.catalog_name AS name
      FROM article a
      GROUP BY a.catalog_id, a.catalog_name
      ORDER BY a.catalog_name ASC`;
    return this.duck.sql(selectCatalog).pipe(map((res: any) => {
      if (DuckDbHelper.hasRes(res)) {
        const catalogs = res.map((r: any) => {
          return new CatalogDto({
            id: r.id,
            name: r.name,
            source: 'CUSTOM',
            enabled: true,
            hasCredentials: false,
          });
        });
        return DuckDbHelper.datas('CatalogDto', catalogs);
      }
      return DuckDbHelper.datas('CatalogDto', []);
    }));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override getById(_org: OrgDto, articleId: string, _add?: Additionals): Observable<WrapperDto<ArticleDto>> {
    const selectArticle = `
      SELECT a.id                     AS id,
             a.number                 AS number,
             a.name                   AS name,
             a.unit                   AS unit,
             a.vat_rate               AS vatRate,
             a.ean                    AS ean,
             a.selling_price_net      AS sellingPriceNet,
             a.selling_price_gross    AS sellingPriceGross,
             a.purchasing_price_net   AS purchasingPriceNet,
             a.purchasing_price_gross AS purchasingPriceGross,
             a.currency_code          AS currencyCode,
             a.old_part               AS oldPart,
             a.dangerous_goods        AS dangerousGoods,
             a.spare_part             AS sparePart,
             a.updatable              AS updatable,
             a.editable               AS editable,
             a.discountable           AS discountable,
             a.external_id            AS externalId,
             a.external_number        AS externalNumber,
             a.goods_groups_id        AS goodsGroupId,
             a.manufacturer           AS manufacturer,
             a.origin_country_code    AS originCountryCode,
             a.notes                  AS notes,
             a.catalog_id             AS catalogId
      FROM article a
      WHERE a.id = ${sqlText(articleId)}`;
    return this.duck.sql(selectArticle).pipe(map((articleRes) => {
      if (DuckDbHelper.hasRes(articleRes)) {
        const article = new ArticleDto(articleRes[0]);
        return DuckDbHelper.data('ArticleDto', article);
      }
      return DuckDbHelper.data('ArticleDto', new ArticleDto());
    }));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchArticleRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const query = this.buildQuery(request);
    const filter = this.buildFilter(request);
    const search = `
      SELECT a.id                  AS id,
             a.number              AS number,
             a.name                AS name,
             a.catalog_id          AS catalogId,
             a.catalog_name        AS catalogName,
             a.selling_price_net   AS sellingPriceNet,
             a.selling_price_gross AS sellingPriceGross,
             a.currency_code       AS currencyCode,
             a.goods_groups_id     AS goodsGroupsId,
             g.name                AS goodsGroupName,
             a.manufacturer        AS manufacturer,
             a.origin_country_code AS originCountryCode,
             FALSE                 AS writable,
             COUNT(*)                 OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING)               AS total_results
      FROM article a
             LEFT JOIN goods_group g ON g.id = a.goods_groups_id
      WHERE ${isDefined(query) ? query : '1=1'}
        AND ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res) => {
      const hasRes = isDefined(res) && res.length > 0;
      const firstRow = hasRes ? res[0] : {total_results: 0} as any;
      const result = new SearchArticleRes({
        items: hasRes ? res : [],
        hits: firstRow.total_results,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchArticleRes', result);
    }));
  }

  private buildSort(request: SearchRequest) {
    let field;
    if (isDefined(request.sortField)) {
      switch (request.sortField) {
        case 'catalogName':
          field = 'a.catalog_name';
          break;
        case 'number':
          field = 'a.number';
          break;
        case 'name':
          field = 'a.name';
          break;
        case 'sellingPriceNet':
          field = 'a.selling_price_net';
          break;
        case 'sellingPriceGross':
          field = 'a.selling_price_gross';
          break;
        case 'goodsGroupName':
          field = 'g.name';
          break;
        case 'manufacturer':
          field = 'a.manufacturer';
          break;
        default:
          this.log.warn('Unhandled sort field "{}"', [request.sortField]);
          field = 'a.catalog_name';
      }
    } else {
      field = 'a.catalog_name';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildQuery(request: SearchRequest): string | undefined {
    if (isDefined(request.query) && nonBlank(request.query)) {
      return `(a.catalog_name ILIKE ${sqlLike(request.query)}
          OR a.number ILIKE ${sqlLike(request.query)}
          OR a.name ILIKE ${sqlLike(request.query)})`;
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private buildFilter(_request: SearchRequest): string | undefined {
    return undefined;
  }
}
